import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flame, 
  Trophy, 
  Target, 
  Calendar,
  Star,
  Heart,
  User,
  Compass,
  Check,
  Lock,
  Sparkles,
  Share2,
  ChevronRight
} from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

// Milestones configuration
const milestones = [
  { days: 7, title: "First Week", icon: Star, description: "Completed Week 1 - Relationship" },
  { days: 14, title: "Halfway There", icon: Target, description: "Two weeks of faithful prayer" },
  { days: 21, title: "Three Weeks", icon: Heart, description: "Building spiritual habits" },
  { days: 30, title: "Journey Complete", icon: Trophy, description: "You did it! Full 30 days" }
];

// Daily challenges (one for each day)
const dailyChallenges = [
  "Spend 5 extra minutes in silence today",
  "Write a gratitude list of 3 things",
  "Pray for someone you find difficult",
  "Fast from social media until noon",
  "Call or text someone you haven't spoken to",
  "Do one act of kindness anonymously",
  "Pray the Rosary or Divine Mercy Chaplet",
  "Read an extra Scripture passage",
  "Write a letter to God",
  "Spend time in nature, noticing God's creation",
  "Practice the Examen twice today",
  "Offer your work as a prayer",
  "Smile at every person you meet",
  "Give away something you no longer need",
  "Listen more than you speak today",
  "Pray for the Holy Father's intentions",
  "Fast from complaining for 24 hours",
  "Write down 3 ways God blessed you this week",
  "Reach out to a friend going through difficulty",
  "Practice silence during a meal",
  "Pray the Stations of the Cross",
  "Do something special for a family member",
  "Spend 10 minutes just listening to God",
  "Forgive someone who hurt you",
  "Make a small sacrifice and offer it up",
  "Write about how you've grown this month",
  "Share your faith journey with someone",
  "Thank God for your struggles",
  "Prepare your heart for the final day",
  "Celebrate! Share your experience with others"
];

const ProgressPage = ({ currentDay, dailyNotes, bookmarkedDays, retreatStartDate }) => {
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Calculate stats
  const completedDays = Object.keys(dailyNotes).filter(k => dailyNotes[k]?.trim()).length;
  const totalWords = Object.values(dailyNotes).reduce((acc, note) => 
    acc + (note?.trim() ? note.split(/\s+/).length : 0), 0
  );
  const bookmarkedCount = Object.keys(bookmarkedDays).filter(k => bookmarkedDays[k]).length;
  
  // Calculate streak
  let currentStreak = 0;
  for (let i = 1; i <= 30; i++) {
    if (dailyNotes[i]?.trim()) currentStreak++;
    else break;
  }
  
  // Calculate longest streak
  let longestStreak = 0;
  let tempStreak = 0;
  for (let i = 1; i <= 30; i++) {
    if (dailyNotes[i]?.trim()) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 0;
    }
  }

  const progressPercent = Math.round((completedDays / 30) * 100);
  const effectiveDay = currentDay || 0;
  const todayChallenge = effectiveDay > 0 && effectiveDay <= 30 ? dailyChallenges[effectiveDay - 1] : null;

  // Get pillar for day
  const getPillar = (day) => {
    if (day <= 7) return { name: 'R', color: 'rose', full: 'Relationship' };
    if (day <= 18) return { name: 'I', color: 'amber', full: 'Identity' };
    if (day <= 25) return { name: 'M', color: 'teal', full: 'Mission' };
    return { name: 'P', color: 'violet', full: 'Purpose' };
  };

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary)] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Trophy className="w-4 h-4" />
          Your Progress
        </div>
        <h1 className="font-heading text-2xl text-[var(--primary)] font-bold">Journey Tracker</h1>
      </div>

      {/* Main Progress Ring */}
      <div className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-6">
          {/* Circular Progress */}
          <div className="relative w-28 h-28 flex-shrink-0">
            <svg className="w-28 h-28 transform -rotate-90">
              <circle
                cx="56"
                cy="56"
                r="48"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="56"
                cy="56"
                r="48"
                stroke="var(--secondary)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${progressPercent * 3.02} 302`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold">{progressPercent}%</span>
              <span className="text-xs text-white/70">Complete</span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <span className="text-white/80 text-sm">Current Streak:</span>
              <span className="font-bold">{currentStreak} days</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-white/80 text-sm">Best Streak:</span>
              <span className="font-bold">{longestStreak} days</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-300" />
              <span className="text-white/80 text-sm">Day:</span>
              <span className="font-bold">{effectiveDay} of 30</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Challenge */}
      {todayChallenge && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-amber-900 text-sm">Today's Challenge</h3>
              <p className="text-amber-800 text-sm mt-1">{todayChallenge}</p>
            </div>
          </div>
        </div>
      )}

      {/* 30-Day Grid */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-3 text-sm">30-Day Overview</h3>
        <div className="grid grid-cols-10 gap-1.5">
          {Array.from({ length: 30 }, (_, i) => {
            const day = i + 1;
            const isCompleted = dailyNotes[day]?.trim();
            const isCurrent = day === effectiveDay;
            const isLocked = day > effectiveDay;
            const pillar = getPillar(day);
            
            return (
              <button
                key={day}
                onClick={() => !isLocked && navigate(`/day/${day}`)}
                disabled={isLocked}
                className={`
                  aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-all
                  ${isCompleted 
                    ? `bg-${pillar.color}-500 text-white shadow-sm` 
                    : isCurrent 
                      ? 'bg-[var(--secondary)] text-white ring-2 ring-[var(--secondary)] ring-offset-2'
                      : isLocked 
                        ? 'bg-gray-100 text-gray-300' 
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }
                `}
              >
                {isCompleted ? <Check className="w-3 h-3" /> : isLocked ? <Lock className="w-2.5 h-2.5" /> : day}
              </button>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-rose-500" /> R
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-500" /> I
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-teal-500" /> M
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-violet-500" /> P
          </span>
        </div>
      </div>

      {/* Milestones */}
      <div className="space-y-3">
        <h3 className="font-heading font-semibold text-[var(--primary)] text-sm px-1">Milestones</h3>
        {milestones.map((milestone) => {
          const isUnlocked = completedDays >= milestone.days;
          const Icon = milestone.icon;
          
          return (
            <div 
              key={milestone.days}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                isUnlocked 
                  ? 'bg-gradient-to-r from-[var(--secondary)]/10 to-[var(--secondary)]/10 border-[var(--secondary)]/30' 
                  : 'bg-gray-50 border-gray-100'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isUnlocked 
                  ? 'bg-gradient-to-br from-[var(--secondary)] to-[var(--secondary)] text-white shadow-lg' 
                  : 'bg-gray-200 text-gray-400'
              }`}>
                {isUnlocked ? <Icon className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold ${isUnlocked ? 'text-[var(--primary)]' : 'text-gray-400'}`}>
                  {milestone.title}
                </h4>
                <p className={`text-sm ${isUnlocked ? 'text-gray-600' : 'text-gray-400'}`}>
                  {milestone.description}
                </p>
              </div>
              {isUnlocked && (
                <div className="text-[var(--secondary)]">
                  <Trophy className="w-5 h-5" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-100">
          <p className="text-2xl font-bold text-rose-600">{completedDays}</p>
          <p className="text-xs text-rose-500">Days</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
          <p className="text-2xl font-bold text-blue-600">{totalWords}</p>
          <p className="text-xs text-blue-500">Words</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-2xl font-bold text-amber-600">{bookmarkedCount}</p>
          <p className="text-xs text-amber-500">Saved</p>
        </div>
      </div>

      {/* Share Progress */}
      <button
        onClick={() => setShowShareModal(true)}
        className="w-full bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white rounded-xl p-4 flex items-center justify-center gap-2 hover:shadow-lg transition-all"
      >
        <Share2 className="w-5 h-5" />
        <span className="font-semibold">Share Your Progress</span>
      </button>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-heading text-xl font-bold text-[var(--primary)] mb-4 text-center">
              Share Your Journey
            </h3>
            <div className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] rounded-xl p-5 text-white text-center mb-4">
              <p className="text-4xl font-bold mb-1">{progressPercent}%</p>
              <p className="text-white/80 text-sm">of 30-Day Lenten Retreat</p>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-sm">{currentStreak} day streak</span>
              </div>
            </div>
            <p className="text-center text-gray-600 text-sm mb-4">
              I'm on Day {effectiveDay} of the RIMP Way Lenten Retreat! 🙏
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `I'm ${progressPercent}% through the 30-Day Lenten Retreat (The RIMP Way)! Currently on Day ${effectiveDay} with a ${currentStreak}-day streak. Join me: ${window.location.origin}`
                  );
                  setShowShareModal(false);
                }}
                className="flex-1 bg-[var(--secondary)] text-white py-2 rounded-lg font-medium"
              >
                Copy Text
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote */}
      <div className="text-center pt-2">
        <p className="text-xs text-gray-400 italic">
          "Progress, not perfection." — St. Augustine
        </p>
      </div>
    </div>
  );
};

export default ProgressPage;
